// Device grants stamped on /users/{uid} by the driver app
// (mobile-app/src/utils/permissionSnapshot.js): permLocation ('always' |
// 'whileInUse' | 'denied' | 'undetermined'), permPreciseLocation,
// permLocationServices, permNotifications, permOverlay,
// permBatteryUnrestricted, permCamera, permCheckedAt. Shared by the Drivers
// list (icon strip) and the driver details "Device" tab.
import LocationOnIcon from '@mui/icons-material/LocationOn';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PhonelinkLockIcon from '@mui/icons-material/PhonelinkLock';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import CameraAltIcon from '@mui/icons-material/CameraAlt';

export const PERM_STATES = { ok: 'ok', warn: 'warn', bad: 'bad', unknown: 'unknown' };

export const PERM_ICONS = {
  location: LocationOnIcon,
  push: NotificationsActiveIcon,
  overlay: PhonelinkLockIcon,
  battery: BatteryChargingFullIcon,
  camera: CameraAltIcon,
};

// green ok / amber partial / red missing / grey not reported
export const permTone = (colors) => ({ ok: colors.GREEN, warn: '#E6A100', bad: colors.RED, unknown: '#9E9E9E' });

// [{ key, label, state, text, fix }] — `fix` is what to tell the driver on
// the phone when the grant is missing (Android wording; iOS is similar).
export const permissionStatus = (row, L) => {
  const r = row || {};
  const yesNo = (v) => (v === true ? PERM_STATES.ok : v === false ? PERM_STATES.bad : PERM_STATES.unknown);
  const unknownText = L('perm_unknown_hint', 'Not reported yet (older app build)');
  const loc = r.permLocation;
  let locState = PERM_STATES.unknown;
  let locText = unknownText;
  if (loc === 'always' || loc === 'whileInUse' || loc === 'denied' || loc === 'undetermined') {
    const labels = { always: L('perm_loc_always', 'Always'), whileInUse: L('perm_loc_while', 'While in use'), denied: L('perm_loc_denied', 'Denied'), undetermined: L('perm_loc_undetermined', 'Not asked') };
    const parts = [labels[loc]];
    if (r.permPreciseLocation === true) parts.push(L('perm_precise', 'precise'));
    if (r.permPreciseLocation === false) parts.push(L('perm_approximate', 'APPROXIMATE only'));
    if (r.permLocationServices === false) parts.push(L('perm_gps_off', 'device location OFF'));
    locText = parts.join(', ');
    if (loc === 'denied' || r.permLocationServices === false) locState = PERM_STATES.bad;
    else if (loc === 'always' && r.permPreciseLocation !== false) locState = PERM_STATES.ok;
    else locState = PERM_STATES.warn;
  }
  const simple = (key, v, label, fix) => ({
    key,
    label,
    state: yesNo(v),
    text: v === true ? L('yes', 'Yes') : v === false ? L('no', 'No') : unknownText,
    fix,
  });
  return [
    {
      key: 'location',
      label: L('perm_location', 'Location'),
      state: locState,
      text: locText,
      fix: L('perm_fix_location', 'Phone Settings → Apps → Donkey Drive → Permissions → Location: "Allow all the time" and "Use precise location". Also switch on Location in the phone\'s quick settings.'),
    },
    simple('push', r.permNotifications, L('perm_push', 'Push notifications'),
      L('perm_fix_push', 'Phone Settings → Apps → Donkey Drive → Notifications: allow.')),
    simple('overlay', r.permOverlay, L('perm_overlay', 'Display over other apps (locked-phone alerts)'),
      L('perm_fix_overlay', 'Phone Settings → Apps → Donkey Drive → "Display over other apps" (or "Appear on top"): allow. Without it a booking cannot be shown on a locked phone. The app also shows an "Allow" row on the driver home.')),
    simple('battery', r.permBatteryUnrestricted, L('perm_battery', 'Battery unrestricted'),
      L('perm_fix_battery', 'Phone Settings → Apps → Donkey Drive → Battery: "Unrestricted" / no optimisation. On Tecno, Infinix, Xiaomi also enable "Autostart".')),
    simple('camera', r.permCamera, L('perm_camera', 'Camera'),
      L('perm_fix_camera', 'Phone Settings → Apps → Donkey Drive → Permissions → Camera: allow (needed for documents and the car photo).')),
  ];
};

// Sort key: how many grants are missing or partial (unknown counts as
// missing so unreported drivers surface too).
export const permissionMissingCount = (row, L) =>
  permissionStatus(row, L).filter((p) => p.state !== PERM_STATES.ok).length;

// One-line text for the CSV export / search.
export const permissionSummary = (row, L) =>
  permissionStatus(row, L).map((p) => `${p.label}: ${p.text}`).join('; ');

// Everything a booking needs to reach a locked phone.
export const lockedPhoneReady = (row, L) => {
  const s = {};
  permissionStatus(row, L).forEach((p) => { s[p.key] = p.state; });
  return s.location === PERM_STATES.ok && s.push === PERM_STATES.ok && s.overlay === PERM_STATES.ok;
};
